
import { defineComponent, ref, Ref, computed, inject, onMounted, watch } from "vue";
import { fabric } from "fabric";
import axios from "axios";
import { store } from "@/store";
import CloseSVG from "@/components/CloseSVG.vue";
import {
  getSheetScale,
  checkBleed,
  setLabelGroupAngle,
  setClipPath,
  addGroups,
} from "@/canvas-api/PreviewCanvas";
import useMountCanvas from "@/composables/useMountCanvas";
import usePrintPreview from "@/composables/usePrintPreview";
import { SheetLabel, ClientDimensions } from "@/types";
import { ActionTypes } from "@/store/actions";

export default defineComponent({
  name: "PrintPreview",
  components: {
    CloseSVG,
  },
  setup() {
    const closeModal = inject("closeModal") as () => void;
    const bounceTransition = inject("bounceTransition") as Ref<string>;
    const fadeTransition = inject("fadeTransition") as Ref<string>;

    const dpi = computed<number>(() => store.getters.dpi);
    const currentLabel = computed<SheetLabel>(() => store.getters.currentLabel);
    const isRotated = computed<boolean>(() =>
      store.getters.canvasOrientation === "rotated" ? true : false
    );
    const clientDimensions = computed<ClientDimensions>(() => store.getters.clientDimensions);

    const title = ref("");
    const loading = ref(false);
    onMounted(() => {
      //Trigger rerender on mount
      title.value = "Label Sheet Layout";

      setupPrintPreview();
    });

    const printPreviewRef = ref<HTMLDivElement>();
    watch(printPreviewRef, (ref: HTMLDivElement | undefined) => {
      if (ref) {
        ref.scrollTop = ref.getBoundingClientRect().height - ref.scrollHeight;
      }
    });

    const modalCanvas = ref<HTMLCanvasElement>();
    const canvas = ref<fabric.Canvas>(new fabric.Canvas(null));
    const canvasWidth = ref(350);
    const canvasHeight = ref(350);

    const sheetHeight = ref(350);
    const sheetWidth = ref(
      sheetHeight.value *
      (currentLabel.value.sheet_size_width / currentLabel.value.sheet_size_height)
    );

    const groupForNode = ref({} as fabric.Group);

    const cloneGroupForServer = (labelGroup: fabric.Group, label: fabric.Object) => {
      const { x: labelLeft, y: labelTop } = label.getCenterPoint();

      labelGroup.clone(function (clone: fabric.Group) {
        clone.set({
          scaleX: 1,
          scaleY: 1,
          originX: "center",
          originY: "center",
        });

        if (isRotated.value) {
          clone.set({
            left: -labelTop + label.getScaledWidth() / 2,
            top: labelLeft + label.getScaledHeight() / 2,
          });
        } else {
          clone.set({
            left: -labelLeft + label.getScaledWidth() / 2,
            top: -labelTop + label.getScaledHeight() / 2,
          });
        }

        groupForNode.value = clone;
      });
    };

    const addSheetRectToCanvas = () => {
      const previewSheet = new fabric.Rect({
        type: "rect",
        name: "previewSheet",
        originX: "center",
        originY: "center",
        stroke: "black",
        strokeWidth: 1,
        left: 0,
        top: 0,
        width: sheetWidth.value,
        height: sheetHeight.value,
        rx: 0,
        ry: 0,
        fill: "white",
        selectable: false,
        evented: false,
        strokeDashArray: undefined,
      });

      canvas.value.add(previewSheet);

      previewSheet.center();

      return previewSheet;
    };

    const setupPrintPreview = async () => {
      fabric.Object.NUM_FRACTION_DIGITS = 8;

      const labelGroup = await usePrintPreview();

      try {
        if (labelGroup) {
          canvas.value = useMountCanvas(
            canvas.value,
            modalCanvas.value,
            canvasWidth.value,
            canvasHeight.value,
            "#f7f9fc"
          );

          const previewSheet = addSheetRectToCanvas();

          setLabelGroupAngle(labelGroup, isRotated.value);
          const label = checkBleed(labelGroup, currentLabel.value.bleed);

          if (label) {
            label.set({
              strokeWidth: 0,
            });

            const topLeftCornerX = previewSheet.calcCoords().tl.x;
            const topLeftCornerY = previewSheet.calcCoords().tl.y;
            const sheetScale = getSheetScale(
              sheetWidth.value,
              currentLabel.value.sheet_size_width,
              dpi.value
            );
            const { x: labelLeft, y: labelTop } = label.getCenterPoint();

            if (isRotated.value) {
              const left = (-labelTop + label.getScaledWidth() / 2) * sheetScale + topLeftCornerX;
              const top = (labelLeft + label.getScaledHeight() / 2) * sheetScale + topLeftCornerY;

              labelGroup.set({
                left,
                top,
                scaleX: sheetScale,
                scaleY: sheetScale,
              });
            } else {
              const left = (-labelLeft + label.getScaledWidth() / 2) * sheetScale + topLeftCornerX;
              const top = (-labelTop + label.getScaledHeight() / 2) * sheetScale + topLeftCornerY;

              labelGroup.set({
                left,
                top,
                scaleX: sheetScale,
                scaleY: sheetScale,
              });
            }

            await setClipPath(
              labelGroup,
              label,
              currentLabel.value.shape_id,
              currentLabel.value.mask_id,
              currentLabel.value.mask_url
            );

            cloneGroupForServer(labelGroup, label);

            addGroups(
              currentLabel.value,
              labelGroup,
              label,
              sheetScale,
              canvas.value,
              sheetWidth.value,
              sheetHeight.value
            );
          } else {
            throw new Error("Missing labelGroup, PrintPreview.vue");
          }
        }
      } catch (e) {
        console.log(e);
      }

      fabric.Object.NUM_FRACTION_DIGITS = 4;
    };

    const printSheet = () => {
      fabric.Object.NUM_FRACTION_DIGITS = 8;

      loading.value = true;

      const cl = currentLabel.value;

      let labelWidth = cl.width;
      let labelHeight = cl.height;
      let leftMargin = cl.left_margin;
      let topMargin = cl.top_margin;
      let horizontalSpacing = cl.horizontal_spacing;
      let verticalSpacing = cl.vertical_spacing;

      if (cl.bleed) {
        if (cl.bleed_size) {
          labelWidth += cl.bleed_size * 2;
          labelHeight += cl.bleed_size * 2;

          leftMargin -= cl.bleed_size;
          topMargin -= cl.bleed_size;

          horizontalSpacing -= cl.bleed_size * 2;
          verticalSpacing -= cl.bleed_size * 2;
        } else {
          labelWidth += 0.125 * 2;
          labelHeight += 0.125 * 2;

          leftMargin -= 0.125;
          topMargin -= 0.125;

          horizontalSpacing -= 0.125 * 2;
          verticalSpacing -= 0.125 * 2;
        }
      }

      let data = JSON.stringify({
        svg: groupForNode.value.toSVG(),
        strokeWidth: 1,
        labelWidth,
        labelHeight,
        leftMargin,
        topMargin,
        horizontalSpacing,
        verticalSpacing,
        labelsPerRow: cl.labels_per_row,
        labelsPerSheet: cl.labels_per_sheet,
        sheetSizeWidth: cl.sheet_size_width,
        sheetSizeHeight: cl.sheet_size_height,
      });

      axios
        .post(process.env.VUE_APP_NODE_URL, data, {
          headers: { "Content-Type": "application/json" },
          responseType: "arraybuffer",
        })
        .then((res) => {
          var blob = new Blob([res.data], {
            type: "application/pdf",
          });
          var fileName = "SheetLabels Label Design.pdf";
          var elem = window.document.createElement("a");
          elem.href = window.URL.createObjectURL(blob);
          elem.download = fileName;
          document.body.appendChild(elem);
          elem.click();
          document.body.removeChild(elem);

          store.dispatch(ActionTypes.SET_MODAL, "printingCompleted");
          loading.value = false;
        })
        .catch((err) => {
          loading.value = false;
          console.log(err);
        });

      fabric.Object.NUM_FRACTION_DIGITS = 4;
    };

    return {
      fadeTransition,
      bounceTransition,
      closeModal,
      printPreviewRef,
      title,
      loading,
      currentLabel,
      modalCanvas,
      canvas,
      canvasWidth,
      canvasHeight,
      printSheet,
      clientDimensions,
    };
  },
});
